import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useMe } from "@/lib/auth";
import { useSwitchOrg } from "@/lib/org";
import { usePartnerMe } from "@/lib/partner";
import { usePlatformMe } from "@/lib/platform";
import { useAgentMe } from "@/lib/support";
import { Spinner } from "@/components/Spinner";

type Props = {
  onPicked: () => void;
  onSingleMembership: () => void;
};

export function OrgPicker({ onPicked, onSingleMembership }: Props) {
  const { t } = useTranslation();
  const me = useMe();
  const switchOrg = useSwitchOrg();
  const navigate = useNavigate();
  // Reseller/commercial users land on the partner console; platform staff
  // land on /platform; support staff land on /support-console. All three can
  // have zero org memberships and must not hit the "no organizations"
  // dead-end.
  const partner = usePartnerMe();
  const platform = usePlatformMe();
  const agent = useAgentMe();
  const isPlatformStaff = !!platform.data?.is_platform_staff;
  // Support-only staff (a platform staffer already routes to /platform above).
  const isSupportStaff =
    !!agent.data?.is_support_staff && !isPlatformStaff;

  useEffect(() => {
    if (partner.isLoading || platform.isLoading || agent.isLoading) return; // wait until known
    if (partner.data) {
      navigate("/partner", { replace: true });
      return;
    }
    if (isPlatformStaff) {
      navigate("/platform", { replace: true });
      return;
    }
    if (isSupportStaff) {
      navigate("/support-console", { replace: true });
      return;
    }
    if (me.data && me.data.memberships.length === 1) {
      onSingleMembership();
    }
  }, [
    me.data,
    partner.data,
    partner.isLoading,
    platform.isLoading,
    agent.isLoading,
    isPlatformStaff,
    isSupportStaff,
    onSingleMembership,
    navigate,
  ]);

  if (me.isLoading || partner.isLoading || platform.isLoading || agent.isLoading)
    return <Spinner fullscreen label={t("ui.orgPickerAuth.loadingOrgs")} />;
  if (partner.data || isPlatformStaff || isSupportStaff)
    return <Spinner fullscreen label={t("ui.orgPickerAuth.loading")} />;

  const memberships = me.data?.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div className="card mx-auto mt-12 max-w-md space-y-3">
        <h1 className="text-xl font-semibold">{t("ui.orgPickerAuth.noOrgsTitle")}</h1>
        <p className="text-sm text-nb-text">
          {t("ui.orgPickerAuth.noOrgsBody")}
        </p>
      </div>
    );
  }

  return (
    <div className="card mx-auto mt-12 max-w-md space-y-4">
      <h1 className="text-xl font-semibold">{t("ui.orgPickerAuth.chooseTitle")}</h1>
      <p className="text-sm text-nb-text-2">
        {t("ui.orgPickerAuth.chooseBody")}
      </p>
      <ul className="divide-y divide-nb-border">
        {memberships.map((m) => (
          <li key={m.id} className="py-3">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-2 py-2 hover:bg-nb-surface"
              onClick={() => {
                switchOrg.mutate(m.org.id, {
                  onSuccess: () => onPicked(),
                });
              }}
            >
              <div className="flex items-center gap-2.5">
                {m.org.logo_url && (
                  <img
                    src={m.org.logo_url}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded object-contain"
                  />
                )}
                <div className="text-left">
                  <div className="font-medium text-nb-text">{m.org.name}</div>
                  <div className="text-xs text-nb-text-2">
                    {m.roles.join(", ") || t("ui.orgPickerAuth.noRoles")}{m.org.country ? ` · ${m.org.country}` : ""}
                  </div>
                </div>
              </div>
              <span className="text-xs text-nb-mint">{t("ui.orgPickerAuth.continue")}</span>
            </button>
          </li>
        ))}
      </ul>
      {switchOrg.isError && (
        <p role="alert" className="text-sm text-cvp-danger">
          {t("ui.orgPickerAuth.switchError")}
        </p>
      )}
    </div>
  );
}